import { Product, TRDSection } from './types';

/**
 * Display price selector — wholesale accounts see the wholesale `price`,
 * everyone else sees the public `retailPrice` (falling back to `msrp` until
 * explicit retail prices are set per SKU).
 */
export const displayPrice = (p: Product, isWholesale: boolean): number =>
  isWholesale ? p.price : (p.retailPrice ?? p.msrp);

/**
 * PRODUCT CATALOG — change log
 *
 * 2026-08-05 (Master Jay) — Pricing round + Okapi launch:
 *   Retail: all machetes $34.99, except 26620 (F1/F2) at $19.99 either size.
 *   Wholesale: all machetes $17.50 (C2's earlier $11.00 override superseded),
 *   except 26620 (F1/F2) at $12.00.
 *   P: Okapi Ratchet Knife added — retail $34.99/knife; wholesale $200 per
 *   10-knife box (entered as $20.00/unit). Mill File pricing unchanged.
 *
 * 2026-09-23 (Master Jay) — Mill File retail set to $9.99 (owner-confirmed).
 *
 * 2026-05-25 (Master Jay) — Blade-size corrections + naming simplification round 2:
 *   D : 1820 Champa (14.5") → 1820C (16")
 *   E : 1820P (14.5") → 1820 (14.5")    [dropped the P]
 *   G : 1004 Natural (21") → 1004 (21") [dropped the Natural]
 *   I1: 152 Natural → 152 Unfinished     [finish rename]
 *   I2: REMOVED  (152 20" not carried — only 18" and 22")
 *   I3: 152 Polished → 152 Unfinished    [finish rename]
 *   L : 202T (19.5") → 202T (18")        [blade size corrected]
 *
 * 2026-05-24 (Master Jay) — Corrected to match labeled Incolma catalog PDF.
 * Source: machete-man-admin catalog-management skill.
 * Earlier 2026-05-24 edit was based on a wrong text reading and was reverted.
 *
 * Authoritative mappings (TMM ID → Incolma Ref / size):
 *   A1: 702W / 20"          A2: 702W / 22"
 *   B1: 448  / 18"          B2: 448  / 21"
 *   C1: 32   / 20"          C2: 32   / 22"
 *   D : 1820 Champa / 14.5"
 *   E : 1820 (non-Champa) / 14.5"
 *   F1: 26620 / 10"         F2: 26620 / 12"
 *   G : 1004 / 21" Natural
 *   H1: 1807 Hooky / 18"    H2: 1807 Hooky / 20"
 *   I1: 152 Natural / 18"   I2: 152 Natural / 20"   I3: 152 Polished / 22"
 *   J1: 127 / 18"           J2: 127 / 22"
 *   K : Buriquito / 14.5"   (replaces Barrigón Pesado)
 *   L : 202T / 19.5"        (no longer Extended Rula 24" — newly added specialty SKU)
 *   M : REMOVED 2026-05-25  (Incolma 1778 placeholder pulled; reinstate when real data available)
 *   N : REMOVED 2026-05-25  (Bamba Katana — not carried)
 *   O : Mill File Box (6", 12 Pack) — blade 6", description + specs corrected for actual 6" bastard-cut files
 */
export const PRODUCTS: Product[] = [
  // A1 - Tres Canales 702W (20")
  {
    id: 'A1',
    sku: 'MM-A1-3CANALES',
    name: 'Incolma 702W (20")',
    category: 'Tres Canales',
    handleMaterial: 'Wood',
    bladeLength: 20,
    weight: 1.6,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 29.99,
    stock: 200,
    image: '/images/products/M1.webp',
    description: 'The Tres Canales is the iconic three-blood-groove Latin machete — straight working profile with three longitudinal fullers that lighten the blade without sacrificing strength. The 20-inch variant is the everyday workhorse: nimble for long days of brush clearing, trail maintenance, and general farm work. Drop-forged carbon steel, full tang.',
    pitch: ['Three-fuller blade', 'Everyday Latin workhorse', 'Brush & trail clearing'],
    specs: { steel: 'SAE 1075', hardness: '52-54 HRC', geometry: 'Full Tang', toughness: 85, retention: 80, flexibility: 80, sharpness: 85 }
  },
  // A2 - Tres Canales (Extended)
  {
    id: 'A2',
    sku: 'MM-A2-3CANALES',
    name: 'Incolma 702W (22")',
    category: 'Tres Canales',
    handleMaterial: 'Wood',
    bladeLength: 22,
    weight: 1.8,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 31.99,
    stock: 150,
    image: '/images/products/M1.webp',
    description: 'Extended-reach Tres Canales — same three-fuller Latin profile as the 20-inch, with extra length for sweeping clearance through dense brush. The longer blade covers more ground per stroke without sacrificing the balanced geometry that\'s made this profile a Colombian standard.',
    pitch: ['Extended reach', 'Three-fuller blade', 'Dense brush clearing'],
    specs: { steel: 'SAE 1075', hardness: '54 HRC', geometry: 'Full Tang', toughness: 82, retention: 82, flexibility: 75, sharpness: 90 }
  },
  // B1 - Rozador (Standard)
  {
    id: 'B1',
    sku: 'MM-B1-ROZADOR',
    name: 'Incolma 448 (18")',
    category: 'Rozador',
    handleMaterial: 'Wood',
    bladeLength: 18,
    weight: 1.6,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 32.99,
    stock: 120,
    image: '/images/products/incolma-448.webp', // real product photo (owner's, 2026-09-23)
    description: 'The Rozador ("clearer") is the dedicated land-clearing machete — a wider, heavier blade than the everyday Peinilla, built for sweeping cuts through scrubland, stubble, weed-heavy fields, and small branches. The 18-inch variant gives controlled reach for tight clearing work and brush maintenance.',
    pitch: ['Land-clearing blade', 'Wider sweeping cuts', 'Scrub & stubble'],
    specs: { steel: 'SAE 1075', hardness: '54 HRC', geometry: 'Full Tang', toughness: 90, retention: 80, flexibility: 60, sharpness: 85 }
  },
  // B2 - Rozador (Compact)
  {
    id: 'B2',
    sku: 'MM-B2-ROZADOR',
    name: 'Incolma 448 (21")',
    category: 'Rozador',
    handleMaterial: 'Wood',
    bladeLength: 21,
    weight: 1.2,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 29.99,
    stock: 100,
    image: '/images/products/incolma-448.webp', // real product photo (owner's, 2026-09-23)
    description: 'The 21-inch Rozador — same wide-bellied clearing profile with extra reach for property-scale brush work. The added length carries more sweeping force per stroke without losing the balanced geometry that defines the Rozador.',
    pitch: ['Extended reach', 'Property-scale clearing', 'Sweeping wide cuts'],
    specs: { steel: 'SAE 1075', hardness: '54 HRC', geometry: 'Full Tang', toughness: 88, retention: 80, flexibility: 65, sharpness: 85 }
  },
  // C1 - Rula (Standard)
  {
    id: 'C1',
    sku: 'MM-C1-RULA',
    name: 'Incolma 32 (20")',
    category: 'Rula',
    handleMaterial: 'Wood',
    bladeLength: 20,
    weight: 1.4,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 29.99,
    stock: 130,
    image: '/images/products/M3.webp',
    description: 'The Rula is engineered for heavy-impact cutting — a wider, weighted blade designed to meet dense vegetation with mass rather than speed. Built for sugar cane, robust forage crops, and tough brush where a lighter machete would deflect. The 20-inch variant gives controlled impact in a manageable length. Drop-forged carbon steel, full tang.',
    pitch: ['Heavy-impact cutting', 'Dense vegetation & cane', 'Weighted Latin blade'],
    specs: { steel: 'SAE 1075', hardness: '52 HRC', geometry: 'Full Tang', toughness: 85, retention: 75, flexibility: 80, sharpness: 85 }
  },
  // C2 - Rula (Extended)
  {
    id: 'C2',
    sku: 'MM-C2-RULA',
    name: 'Incolma 32 (22")',
    category: 'Rula',
    handleMaterial: 'Wood',
    bladeLength: 22,
    weight: 1.6,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 31.99,
    stock: 80,
    image: '/images/products/M3.webp',
    description: 'The 22-inch Rula extends the heavy-impact geometry with extra length for cane fields and dense brush work. The added mass at the tip delivers crushing chops through tough stalks and resistant vegetation. A working tool for serious cane and clearing operations.',
    pitch: ['Extended reach', 'Heavy-impact chops', 'Cane & dense brush'],
    specs: { steel: 'SAE 1075', hardness: '52 HRC', geometry: 'Full Tang', toughness: 85, retention: 75, flexibility: 78, sharpness: 85 }
  },
  // D - Incolma 1820C (16")
  {
    id: 'D',
    sku: 'MM-D-BARRIGON',
    name: 'Incolma 1820C (16")',
    category: 'Champa',
    handleMaterial: 'Wood',
    bladeLength: 16,
    weight: 1.8,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 34.99,
    stock: 180,
    image: '/images/products/incolma-1820c.webp', // real product photo (owner's, 2026-09-24)
    description: 'A compact heavy-belly working blade — pronounced forward weight delivers impact-heavy chopping in a controlled package. Suited to dense brush, sugar cane, and field-clearing work where mass at the tip does the cutting. Full-tang Incolma construction, drop-forged carbon steel.',
    pitch: ['Heavy belly curve', 'Sugar cane & brush', 'Mass-driven chops'],
    specs: { steel: 'SAE 1075', hardness: '54-56 HRC', geometry: 'Full Tang', toughness: 92, retention: 80, flexibility: 65, sharpness: 85 }
  },
  // E - Pacora 1820 (14.5")
  {
    id: 'E',
    sku: 'MM-E-PACORA',
    name: 'Incolma 1820 (14.5")',
    category: 'Pacora',
    handleMaterial: 'Wood',
    bladeLength: 14.5,
    weight: 1.3,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 32.99,
    stock: 60,
    image: '/images/products/M4.webp',
    description: 'A compact agricultural workhorse with a moderate belly curve — sized for orchard, smallhold, and homestead use. Lighter than a full-size cane machete, with enough tip mass for solid chopping through fruit-tree branches and dense undergrowth. The shorter blade favors control over reach.',
    pitch: ['Compact workhorse', 'Orchard & smallhold', 'Control over reach'],
    specs: { steel: 'SAE 1075', hardness: '52-54 HRC', geometry: 'Full Tang', toughness: 80, retention: 85, flexibility: 75, sharpness: 90 }
  },
  // F1 - Bananera (Standard)
  {
    id: 'F1',
    sku: 'MM-F1-BANANERA',
    name: 'Incolma 26620 (10")',
    category: 'Bananera',
    handleMaterial: 'Wood',
    bladeLength: 10,
    weight: 0.9,
    price: 12.00,
    retailPrice: 19.99,
    msrp: 27.99,
    stock: 200,
    image: '/images/products/incolma-26620-2.webp', // real product photo (owner's improved re-shoot, 2026-09-23)
    description: 'The Machetilla Bananera ("little banana machete") is Colombia\'s compact banana-harvesting specialist. The short 10-inch blade gives precise control for cutting banana and plantain bunches close to the stem without damaging the plant. Drop-forged carbon steel, full tang, sized for tight-row plantation work.',
    pitch: ['Banana & plantain harvest', 'Precise close-work', 'Compact 10" blade'],
    specs: { steel: 'SAE 1075', hardness: '54 HRC', geometry: 'Full Tang', toughness: 75, retention: 85, flexibility: 70, sharpness: 92 }
  },
  // F2 - Bananera (Extended)
  {
    id: 'F2',
    sku: 'MM-F2-BANANERA',
    name: 'Incolma 26620 (12")',
    category: 'Bananera',
    handleMaterial: 'Wood',
    bladeLength: 12,
    weight: 1.0,
    price: 12.00,
    retailPrice: 19.99,
    msrp: 29.99,
    stock: 200,
    image: '/images/products/incolma-26620-2.webp', // real product photo (owner's improved re-shoot, 2026-09-23)
    description: 'The 12-inch Bananera is the standard size for banana and plantain harvest across Caribbean and Colombian plantations. Slightly more reach than the 10-inch with the same precise control for fruit-bunch work. Wood handle for traditional grip in humid plantation conditions.',
    pitch: ['Standard banana size', 'Plantation harvest', 'Wood-handled control'],
    specs: { steel: 'SAE 1075', hardness: '54 HRC', geometry: 'Full Tang', toughness: 75, retention: 85, flexibility: 70, sharpness: 92 }
  },
  // G - Guaduero 1004 (21") Natural
  {
    id: 'G',
    sku: 'MM-G-GUADUERO',
    name: 'Incolma 1004 (14")',
    category: 'Guaduero',
    handleMaterial: 'Wood',
    bladeLength: 14,
    weight: 2.0,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 38.99,
    stock: 100,
    image: '/images/products/M7.webp',
    description: 'The Pacora con gancho ("Pacora with hook") — named after a Colombian Andean town but its working life is coastal. Incolma\'s official use cases are peeling coconut, cutting sugar cane, and processing fish. The distinctive hooked tip pulls vegetation toward the cutting edge. A multi-purpose work blade for retailers serving Caribbean and Pacific-coast customers — fishermen, cane workers, and coconut harvesters.',
    pitch: ['Hooked tip', 'Coconut · cane · fish', 'Coastal multi-use'],
    specs: { steel: 'SAE 1075', hardness: '56 HRC', geometry: 'Full Tang', toughness: 95, retention: 80, flexibility: 45, sharpness: 80 }
  },
  // H1 - Cuma/Hawkbill (Standard)
  {
    id: 'H1',
    sku: 'MM-H1-CUMA',
    name: 'Incolma 1807 (18")',
    category: 'Cuma',
    handleMaterial: 'Wood',
    bladeLength: 18,
    weight: 1.2,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 29.99,
    stock: 90,
    image: '/images/products/M8.webp',
    description: 'A specialty harvest blade with a sharply curved hooked tip — designed for pull-cut work that draws stalked vegetation toward the cutting edge. Suited to rice, wheat, and grain harvesting where pull cuts are more efficient than push cuts over long harvest days.',
    pitch: ['Curved hooked tip', 'Grain & stalk harvest', 'Pull-cut motion'],
    specs: { steel: 'SAE 1075', hardness: '54 HRC', geometry: 'Full Tang', toughness: 85, retention: 80, flexibility: 70, sharpness: 90 }
  },
  // H2 - Cuma/Hawkbill (Extended)
  {
    id: 'H2',
    sku: 'MM-H2-CUMA',
    name: 'Incolma 1807 (20")',
    category: 'Cuma',
    handleMaterial: 'Wood',
    bladeLength: 20,
    weight: 1.4,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 31.99,
    stock: 80,
    image: '/images/products/M8.webp',
    description: 'The 20-inch Hooky extends the curved-tip profile for deeper reach into crop stands. The longer hook grabs more vegetation per stroke, improving efficiency for commercial grain operations and large-acreage stalked-crop harvests.',
    pitch: ['Extended reach', 'Commercial harvest', 'Larger hook curve'],
    specs: { steel: 'SAE 1075', hardness: '54 HRC', geometry: 'Full Tang', toughness: 85, retention: 80, flexibility: 70, sharpness: 90 }
  },
  // I1 - Peinilla Angosta (Plastic 18")
  {
    id: 'I1',
    sku: 'MM-I1-RECTO',
    name: 'Incolma 152 Unfinished (18")',
    category: 'Peinilla Angosta',
    handleMaterial: 'Plastic',
    bladeLength: 18,
    weight: 1.5,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 34.99,
    stock: 150,
    image: '/images/products/M9.webp',
    description: 'The Peinilla Angosta ("narrow comb") is the everyday Colombian campesino blade — narrow, light, indispensable. Used across coffee zones, smallhold farms, and roadside work for clearing, weeding, trail work, and general agriculture. Raw-forged natural finish develops a working patina with use; plastic handle for all-weather grip.',
    pitch: ['Narrow Peinilla', 'Everyday clearing blade', 'Raw-forged natural'],
    specs: { steel: 'SAE 1075', hardness: '54-56 HRC', geometry: 'Full Tang', toughness: 88, retention: 85, flexibility: 70, sharpness: 88 }
  },
  // I2 - REMOVED 2026-05-25 (Incolma 152 20" not carried; only 18" and 22" Unfinished)
  // I3 - Peinilla Angosta (Plastic 22")
  {
    id: 'I3',
    sku: 'MM-I3-RECTO',
    name: 'Incolma 152 Unfinished (22")',
    category: 'Peinilla Angosta',
    handleMaterial: 'Plastic',
    bladeLength: 22,
    weight: 1.9,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 38.99,
    stock: 100,
    image: '/images/products/M9.webp',
    description: 'Maximum-reach 22-inch Peinilla Angosta — the longest in the narrow-comb series. Built for property maintenance, fence-line clearing, and roadside work where extra reach reduces effort per pass. Polished finish, plastic handle for wet-weather grip.',
    pitch: ['Maximum reach', 'Property maintenance', 'Polished finish'],
    specs: { steel: 'SAE 1075', hardness: '54-56 HRC', geometry: 'Full Tang', toughness: 88, retention: 85, flexibility: 70, sharpness: 88 }
  },
  // J1 - Incolma 127 (Polished Latin 18")
  {
    id: 'J1',
    sku: 'MM-J1-COLIMA',
    name: 'Incolma 127 (18")',
    category: 'Polished Latin',
    handleMaterial: 'Plastic',
    bladeLength: 18,
    weight: 1.3,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 32.99,
    stock: 150,
    image: '/images/products/M10.webp',
    description: 'The Incolma 127 is a classic polished Latin machete — single-edge profile with a forward-belly curve that delivers solid chopping power for everyday agricultural work. The 18-inch length is the balanced everyday workhorse: short enough for controlled use, long enough for vegetation clearing, harvesting, and general farm tasks. Mirror-polished blade resists corrosion in humid conditions; textured plastic handle holds grip in wet weather.',
    pitch: ['Polished single-edge', 'Forward belly chop', 'All-weather grip'],
    specs: { steel: 'SAE 1075', hardness: '52-54 HRC', geometry: 'Full Tang', toughness: 80, retention: 80, flexibility: 75, sharpness: 90 }
  },
  // J2 - Incolma 127 (Polished Latin 22")
  {
    id: 'J2',
    sku: 'MM-J2-COLIMA',
    name: 'Incolma 127 (22")',
    category: 'Polished Latin',
    handleMaterial: 'Plastic',
    bladeLength: 22,
    weight: 1.5,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 34.99,
    stock: 150,
    image: '/images/products/M10.webp',
    description: 'The 22-inch Incolma 127 extends the classic polished Latin profile for longer-reach work. Same single-edge geometry and forward-belly curve as the 18-inch, with extra blade length for property maintenance, fence-line clearing, and faster ground coverage. Mirror-polished finish and textured plastic grip stand up to humid plantation and roadside conditions.',
    pitch: ['Extended reach', 'Polished single-edge', 'Property work'],
    specs: { steel: 'SAE 1075', hardness: '52-54 HRC', geometry: 'Full Tang', toughness: 80, retention: 80, flexibility: 75, sharpness: 90 }
  },
  // K - Buriquito (14.5")
  {
    id: 'K',
    sku: 'MM-K-PESADO',
    name: 'Buriquito (14.5")',
    category: 'Buriquito',
    handleMaterial: 'Plastic',
    bladeLength: 14.5,
    weight: 2.2,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 42.99,
    stock: 75,
    image: '/images/products/M11.webp',
    description: 'The Buriquito ("little donkey") is a short, wide-bladed working blade with a deep belly — built for heavy chopping where a full-size machete is too much. Originally a Dominican Republic style, the Buriquito splits kindling, hews wood, and handles utility work that needs mass over reach. Compact 14.5" profile, drop-forged carbon steel, plastic handle.',
    pitch: ['Heavy chopper', 'Dominican-style profile', 'Mass over reach'],
    specs: { steel: 'SAE 1075', hardness: '56-58 HRC', geometry: 'Full Tang', toughness: 95, retention: 82, flexibility: 45, sharpness: 80 }
  },
  // L - Incolma 202T (18") — specialty SKU
  {
    id: 'L',
    sku: 'MM-L-202T',
    name: 'Incolma 202T (18")',
    category: 'Specialty',
    handleMaterial: 'Wood',
    bladeLength: 18,
    weight: 1.7,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 36.99,
    stock: 60,
    image: '/images/products/incolma-202T.webp',
    description: 'Incolma 202T — 18-inch specialty blade in our spring 2026 line. Full-tang construction, drop-forged carbon steel, plastic handle for all-weather grip. Detailed product specifications available on request via WhatsApp +1 (416) 684-0010.',
    pitch: ['Spring 2026 addition', '18" specialty blade', 'Full tang construction'],
    specs: { steel: 'SAE 1075', hardness: '54 HRC', geometry: 'Full Tang', toughness: 85, retention: 80, flexibility: 70, sharpness: 85 }
  },
  // M - Tranzidor 45 (18") — specialty saw + machete combo
  {
    id: 'M',
    sku: 'MM-M-TRANZIDOR',
    name: 'Tranzidor 45 (18")',
    category: 'Tranzidor 45',
    handleMaterial: 'Plastic',
    bladeLength: 18,
    weight: 1.5,
    price: 17.50,
    retailPrice: 34.99,
    msrp: 42.99,
    stock: 50,
    image: '/images/products/tranzidor-45.webp', // real product photo (owner's, 2026-09-23)
    description: 'The Tranzidor 45 is a dual-function field tool — sharpened machete edge on one side, serrated saw teeth along the spine. Use the blade for clearing and chopping; use the saw for cutting branches and small timber. 18-inch black-finished blade, yellow ergonomic plastic handle, full-tang Incolma construction. Built for field-utility work where retailers want one tool that does two jobs.',
    pitch: ['Dual-function field tool', 'Saw + machete in one', '18" black-finished blade'],
    specs: { steel: 'SAE 1075', hardness: '54-56 HRC', geometry: 'Full Tang', toughness: 92, retention: 82, flexibility: 60, sharpness: 85 }
  },
  // N removed 2026-05-25 (Bamba Katana — not carried)
  // O - Mill File (6") — single sharpening file. Split from the old 12-pack box to
  // individual sale 2026-06-24 (Master Jay). Retail $9.99 owner-confirmed 2026-09-23;
  // wholesale $4.25 is still the interim per-unit figure from the old box.
  {
    id: 'O',
    sku: 'MM-O-MILLFILE',
    name: 'Mill File (6")',
    category: 'Mill File',
    handleMaterial: 'Ergonomic Polymer',
    bladeLength: 6,
    weight: 0.3,
    price: 4.25,
    retailPrice: 9.99,
    msrp: 7.50,
    stock: 600,
    image: '/images/products/M13.webp',
    description: 'A single 6-inch bastard-cut mill file for sharpening Incolma machete blades. Hardened tool-steel teeth on an ergonomic polymer grip — the essential maintenance tool to restore a working edge and keep your blade field-sharp.',
    pitch: ['6" bastard-cut file', 'Hardened tool steel', 'Sharpens any machete'],
    specs: { steel: 'High Carbon Tool Steel', hardness: '65 HRC', geometry: '6" Bastard Cut', toughness: 100, retention: 100, flexibility: 0, sharpness: 100 }
  },
  // P - Okapi Ratchet Knife — first non-Incolma line (owner's own stock, added 2026-08-05).
  // Wholesale is sold by the box: 10 knives per box at $200 ($20.00/knife below).
  {
    id: 'P',
    sku: 'MM-P-OKAPI',
    name: 'Okapi Ratchet Knife',
    category: 'Okapi Ratchet',
    handleMaterial: 'Wood',
    bladeLength: 4,
    weight: 0.3,
    price: 20.00,
    retailPrice: 34.99,
    msrp: 34.99,
    stock: 100,
    image: '/images/products/okapi-ratchet-2.webp', // real product photo (owner's, 2026-09-23); replaced the AI placeholder
    description: 'The Okapi ratchet knife — the folding work knife of the Caribbean. Carbon-steel blade, wooden handle, and the signature ring-pull ratchet lock that clicks open and holds firm. An Okapi in the pocket is as Jamaican as the machete on the wall: harvest work, rope, grafting, and everyday utility. Wholesale accounts: sold by the box of 10 knives.',
    pitch: ['Ring-pull ratchet lock', 'Caribbean pocket classic', 'Carbon-steel folder'],
    specs: { steel: '1055 Carbon', hardness: '~55 HRC', geometry: 'Ratchet lockback folder', toughness: 70, retention: 78, flexibility: 40, sharpness: 88 }
  }
];

export const TRD_SECTIONS: TRDSection[] = [];